import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { JobsAndRatesPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import JobsAndRatesDataCollection from 'modules/project_management/components/tables/JobsAndRatesDataCollection'
import { ProjectsUpsellRequestSources } from 'modules/project_management/components/upsells/constants'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

const JobsAndRatesPage = () => {
  const { company } = useCurrentSession()

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openAddSidepanel, setOpenAddSidepanel] = useState(false)
  const { data, loading, error } = useQuery(JobsAndRatesPageDocument, {
    variables: { companyId: company.id },
  })

  const canManageRates = !!data?.projectManagement._policies.rateManage.can
  const canSeeJobsAndRatesPage = !!data?.projectManagement._policies.pagesSeeJobsAndRates.can
  const canSeeJobsAndRatesUpsell =
    !!data?.projectManagement._policies.pagesSeeJobsAndRatesUpsell.can

  const isLegacyJobCatalogEnabled =
    data?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const isJobCatalogTreeEnabled = !isLegacyJobCatalogEnabled

  useEffect(() => {
    if (canSeeJobsAndRatesPage) {
      track(trackingEvents.projectManagement.jobsAndRates.visitJobsAndRatesSection)

      const openSidepanel = searchParams.get('openAddSidepanel')
      if (openSidepanel === 'true') {
        track(trackingEvents.projectManagement.jobsAndRates.clickOnDefineBillRateLink)

        setOpenAddSidepanel(true)
        // Remove the parameter from URL after opening sidepanel
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev)
          newParams.delete('openAddSidepanel')
          return newParams
        })
      }
    }
  }, [searchParams, setSearchParams, canSeeJobsAndRatesPage])

  return (
    <ResponsivePage.Body
      variant="wide"
      loading={!data && loading}
      error={error}
      authorized={canSeeJobsAndRatesUpsell || canSeeJobsAndRatesPage}
    >
      {canSeeJobsAndRatesUpsell ? (
        <Box style={{ height: '50vh' }}>
          <UpsellingKit.EmptyState
            moduleRequested={AvailableModules.projects}
            featureRequested={Features.projects_costs}
            requestSource={ProjectsUpsellRequestSources.jobsAndRatesUpsellState}
            requestReason={ValidReasons.MissingFeature}
            title={i18n.t('project_management.jobs_and_rates.upsell_state.title')}
            description={i18n.t('project_management.jobs_and_rates.upsell_state.description')}
          />
        </Box>
      ) : (
        <>
          <SectionHeader
            title={i18n.t('project_management.jobs_and_rates.header.title')}
            description={i18n.t('project_management.jobs_and_rates.header.description')}
            link={{
              href: `https://help.factorialhr.com/${i18n.t(
                'project_management.projects.lower_case_title'
              )}`,
              label: i18n.t('project_management.jobs_and_rates.header.secondary_button'),
            }}
            action={{
              icon: ExternalLink,
              label: i18n.t('project_management.jobs_and_rates.header.main_button'),
              onClick: () => {
                track(trackingEvents.projectManagement.jobsAndRates.clickOnSeeAllJobsButton)
                navigate(navigation.employees.jobCatalog.fullPath())
              },
            }}
          />
          <JobsAndRatesDataCollection
            canManageRates={canManageRates}
            openAddSidepanel={openAddSidepanel}
            isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
            setOpenAddSidepanel={setOpenAddSidepanel}
          />
        </>
      )}
    </ResponsivePage.Body>
  )
}

export default JobsAndRatesPage
